// ============================================================================
// Hooks TanStack Query — ADMIN AD-D2 : configs remuneration GLOBALES (#12-15)
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, canAccessGestAdmin admin SEUL) ; le bundle vit sous ADMIN_ROOT_KEY.
// Les mutations invalident la famille admin ENTIERE (['gest','admin']) ET la
// grille CE (['gest','grille-ce']) : les configs (cout vehicule, revenue_pct,
// cibles d'equipe, echelons) alimentent la masse salariale reelle et les
// graphiques (lecon AVG-02). confirmEchelon touche aussi une fiche individuelle
// -> familles gestion/profil invalidees pour cette mutation.
// Pourcentages : l'API parle en FRACTIONS (0..1, parite stockage PoC) — la
// conversion points <-> fractions vit au composant (parite toPoints PoC).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (routes/admin.ts AD-D2) ────────────

export interface AdminEvalCriterion {
  key: string;
  label: string;
  weight: number;
}

export interface AdminEvalConfig {
  passThreshold: number;
  maxScore: number;
  criteria: AdminEvalCriterion[];
}

/** Miroir AdminYearConfig serveur (fusion cout vehicule + cible revenus). */
export interface AdminYearConfig {
  year: number;
  vehicleCostBiweekly: number | null;
  revenuePct: number | null;
}

/** Miroir AdminPayrollTeamTarget serveur (fraction 0..1). */
export interface AdminPayrollTeamTarget {
  year: number;
  teamId: string;
  targetPct: number;
}

/** Miroir AdminPendingEchelon serveur (P-D2-4). */
export interface AdminPendingEchelon {
  evaluationId: string;
  email: string;
  year: number;
  score: number;
  submittedAt: string;
  offGrid: boolean;
}

export interface AdminRemunerationConfigResponse {
  viewer: { role: GestAdminRole; email: string };
  evalConfig: AdminEvalConfig | null;
  yearConfigs: AdminYearConfig[];
  teamTargets: AdminPayrollTeamTarget[];
  pending: AdminPendingEchelon[];
  year: number | null;
  yearConfig: AdminYearConfig | null;
}

export interface EvalConfigPutPayload {
  passThreshold: number;
  maxScore: number;
  /** Textarea brut « un critere par ligne » ; undefined = criteres NON touches (P-D2-2). */
  criteria?: string;
}

export interface YearConfigPutPayload {
  year: number;
  vehicleCostBiweekly?: number;
  revenuePct?: number;
}

export interface PayrollTargetsPutPayload {
  year: number;
  /** Fraction 0..1 (l'UI saisit des points 0..100). */
  revenuePct: number;
  targets: { teamId: string; targetPct: number }[];
}

export interface ConfirmEchelonPayload {
  email: string;
  year: number;
}

export interface ConfirmEchelonResponse {
  ok: boolean;
  applied: boolean;
  from: number | null;
  to: number | null;
}

const remunerationConfigKey = [...ADMIN_ROOT_KEY, 'remuneration-config'] as const;

/**
 * Code court d'erreur mutation -> message i18n (parite adminMemberErrorCode).
 * Codes serveur connus : targets_sum (coherence PoC 869-871), echelon_off_grid,
 * echelon_no_compensation, forbidden, notfound, invalid.
 */
export function remunerationConfigErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

/** AD-D2 : bundle CONFIGS GLOBALES de l'onglet Remuneration (1 requete SPA). */
export function useAdminRemunerationConfig(enabled: boolean) {
  return useQuery({
    queryKey: remunerationConfigKey,
    queryFn: () => api.get<AdminRemunerationConfigResponse>('/api/admin/remuneration/config'),
    enabled,
    retry: false,
  });
}

/**
 * Invalidation commune : famille admin + grille CE (les configs alimentent la
 * masse reelle et les graphiques — AVG-02, MASTER §e).
 */
function useInvalidateRemunerationConfig() {
  const qc = useQueryClient();
  return (alsoIndividual = false) => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
    void qc.invalidateQueries({ queryKey: ['gest', 'grille-ce'] });
    if (alsoIndividual) {
      // confirmEchelon change la fiche (current_echelon) : les vues gestion et
      // profil de la cible doivent se rafraichir.
      void qc.invalidateQueries({ queryKey: ['gest', 'management'] });
      void qc.invalidateQueries({ queryKey: ['gest', 'profile'] });
    }
  };
}

/** #12 : seuil/max/criteres (parsing 1-par-ligne serveur). Audit eval_config.changed. */
export function useUpsertEvalConfig() {
  const invalidate = useInvalidateRemunerationConfig();
  return useMutation({
    mutationFn: (payload: EvalConfigPutPayload) =>
      api.put<{ ok: boolean }>('/api/admin/remuneration/eval-config', payload),
    onSuccess: () => invalidate(),
  });
}

/** #13 : cout vehicule (et/ou cible revenus) par annee — PUT partiel anti-effacement. */
export function useUpsertYearConfig() {
  const invalidate = useInvalidateRemunerationConfig();
  return useMutation({
    mutationFn: (payload: YearConfigPutPayload) =>
      api.put<{ ok: boolean }>('/api/admin/remuneration/year-config', payload),
    onSuccess: () => invalidate(),
  });
}

/** #14 : cibles de masse (remplacement d'ensemble par annee, coherence targets_sum serveur). */
export function useUpsertPayrollTargets() {
  const invalidate = useInvalidateRemunerationConfig();
  return useMutation({
    mutationFn: (payload: PayrollTargetsPutPayload) =>
      api.put<{ ok: boolean }>('/api/admin/remuneration/payroll-targets', payload),
    onSuccess: () => invalidate(),
  });
}

/** #15 : confirme une montee d'echelon proposee (append-only serveur, P-D2-1). */
export function useConfirmEchelon() {
  const invalidate = useInvalidateRemunerationConfig();
  return useMutation({
    mutationFn: (payload: ConfirmEchelonPayload) =>
      api.post<ConfirmEchelonResponse>('/api/admin/remuneration/echelons/confirm', payload),
    onSuccess: () => invalidate(true),
  });
}
